import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import { removeFromCart } from '../store/cartSlice';
import { HiTrash } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const Cart = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const cartItems = useSelector((state: RootState) => state.cart.items);
  const { t } = useTranslation();

  const getTotalPrice = () => {
    return cartItems.reduce((total, item) => total + item.posterConfiguration.product.price, 0);
  };

  const formatPrice = (price: number) => {
    return price.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
  };

  return (
    <div className="w-full w-[700px] max-w-[700px] mx-auto px-4 space-y-6 text-white">
      {/* Header */}
      <div className="space-y-1 mb-6">
        <h1 className="text-2xl font-semibold font-sans">{t('cart.title', 'Panier')}</h1>
        <p className="text-gray-400 font-light text-sm">
          {t('cart.subtitle', 'Vérifiez vos articles avant de finaliser la commande.')}
        </p>
      </div>

      {cartItems.length === 0 ? (
         <div className="text-center py-16 text-gray-400 bg-[#2a2a2a] rounded-md">
            <p className="font-sans text-lg mb-4">{t('cart.empty', 'Votre panier est vide.')}</p>
            <button
              onClick={() => navigate('/')} 
              className="px-6 py-2.5 text-white text-sm font-medium rounded-sm transition-colors duration-150 uppercase tracking-wider cursor-pointer shadow-md"
              style={{ backgroundColor: '#FC4C02' }}
              onMouseOver={e => (e.currentTarget.style.backgroundColor = '#d63a00')}
              onMouseOut={e => (e.currentTarget.style.backgroundColor = '#FC4C02')}
            >
              {t('cart.startCreation', 'Commencer la création')}
            </button>
         </div>
      ) : (
        <div className="space-y-5">
          {cartItems.map((item) => (
            <React.Fragment key={item.id}>
              <div className="flex flex-col bg-[#333333] rounded-sm shadow-lg overflow-hidden border border-gray-700">
                <div
                  className="w-full rounded-t-sm flex items-center justify-center py-5"
                  style={{
                    aspectRatio: "0.707",
                    minHeight: 120,
                    maxHeight: 260,
                    background: "#222"
                  }}
                >
                  {item.thumbnailUrl ? (
                    <img
                      src={item.thumbnailUrl}
                      alt={t('cart.previewAlt', "Aperçu de l'affiche")}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <span className="text-center">{t('cart.noPreview', 'Aperçu indisponible')}</span>
                  )}
                </div>
                <div className="p-4 flex flex-col flex-grow">
                  <div 
                    className="font-semibold font-sans text-lg mb-1 truncate cart-item-title"
                    dangerouslySetInnerHTML={{ __html: item.posterConfiguration.labels.title.text || t('cart.defaultTitle', 'Affiche personnalisée') }}
                  />
                  <div
                    className="text-xs text-gray-400 font-light leading-snug mb-3 cart-item-details"
                    dangerouslySetInnerHTML={{ __html: item.posterConfiguration.labels.description?.text || '' }}
                  />
                  <div className="mt-auto pt-2 flex items-center justify-between">
                    <p className="text-base font-semibold font-sans">{formatPrice(item.posterConfiguration.product.price)}</p>
                    <button
                      onClick={() => dispatch(removeFromCart(item.id))}
                      className="text-gray-500 hover:text-red-500 transition-colors p-1 cursor-pointer"
                      aria-label={t('cart.removeItem', "Supprimer l'article")}
                    >
                      <HiTrash className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            </React.Fragment>
          ))}

          {/* Cart Summary & Checkout Button */}
          <div className="pt-8 mt-8 border-t border-gray-600 space-y-5">
             <div className="flex justify-between items-center text-base font-semibold font-sans">
                <span>{t('cart.total', 'Total :')}</span>
                <span>{formatPrice(getTotalPrice())}</span>
             </div>
              <button
                onClick={() => navigate('/checkout')}
                className="w-full bg-orange-500 hover:opacity-85 text-white py-3 rounded-sm text-xs font-sans font-medium transition duration-150 uppercase tracking-wider shadow-md cursor-pointer"
            >
                {t('cart.checkout', 'Finaliser la commande')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cart;